import { CampaignOptimizationValue, Goal, GoalMetrics } from '@app/core/models/common.models';
import { Currency } from '@app/core/models/customer.models';
import {
  CLICKS,
  CONVERSIONS,
  COST,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_CLICKS,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_CLICKS,
  PG_GOAL_METRIC_CLICKS_PER_DAY,
  PG_GOAL_METRIC_CLICKS_PER_MONTH,
  PG_GOAL_METRIC_CONVERSIONS_PER_MONTH,
  PG_GOAL_METRIC_COST_PER_CLICK,
  PG_GOAL_METRIC_COST_PER_CONVERSION,
  PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS,
  PG_GOAL_METRIC_COST_PER_DAY,
  PG_GOAL_METRIC_COST_PER_MONTH,
  PG_GOAL_METRIC_CRR,
  PG_GOAL_METRIC_CRR_CLICKS,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PG_GOAL_METRIC_ROAS,
  PG_GOAL_METRIC_ROAS_CLICKS,
  PG_GOAL_METRIC_ROI,
  PROFIT,
  RETURN_ON_INVESTMENT,
  ROAS,
  ROAS_CLICKS,
} from '@app/core/models/metrics.constants';
import {
  AxisGridInfo,
  CHART_COLORS,
  CHART_SCALE_WIDTH,
  ChartMarkers,
  ChartResetDefaults,
} from '@app/shared/modules/charts/chart.models';

export const mapMetricsToGoalType = {
  [PG_GOAL_METRIC_CLICKS_PER_DAY]: CLICKS,
  [PG_GOAL_METRIC_CLICKS_PER_MONTH]: CLICKS,
  'CONVERSIONS-PER-DAY': CONVERSIONS,
  [PG_GOAL_METRIC_CONVERSIONS_PER_MONTH]: CONVERSIONS,
  [PG_GOAL_METRIC_COST_PER_CONVERSION]: COST_PER_CONVERSION,
  [PG_GOAL_METRIC_COST_PER_DAY]: COST,
  [PG_GOAL_METRIC_COST_PER_MONTH]: COST,
  [PG_GOAL_METRIC_COST_PER_CLICK]: COST_PER_CLICK,
  [PG_GOAL_METRIC_CRR]: COST_REVENUE_RATIO,
  [PG_GOAL_METRIC_ROI]: RETURN_ON_INVESTMENT,
  [PG_GOAL_METRIC_ROAS]: ROAS,
  [PG_GOAL_METRIC_PROFIT_MAXIMIZATION]: PROFIT,
  [PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS]: COST_PER_CONVERSION_CLICKS,
  [PG_GOAL_METRIC_CRR_CLICKS]: COST_REVENUE_RATIO_CLICKS,
  [PG_GOAL_METRIC_ROAS_CLICKS]: ROAS_CLICKS,
};

export const mapGoalTypeToMetrics = {
  [COST]: PG_GOAL_METRIC_COST_PER_DAY,
  [CLICKS]: PG_GOAL_METRIC_CLICKS_PER_DAY,
  [COST_PER_CLICK]: PG_GOAL_METRIC_COST_PER_CLICK,
  [COST_PER_CONVERSION]: PG_GOAL_METRIC_COST_PER_CONVERSION,
  [COST_REVENUE_RATIO]: PG_GOAL_METRIC_CRR,
  [RETURN_ON_INVESTMENT]: PG_GOAL_METRIC_ROI,
  [ROAS]: PG_GOAL_METRIC_ROAS,
  [PROFIT]: PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  [COST_PER_CONVERSION_CLICKS]: PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS,
  [COST_REVENUE_RATIO_CLICKS]: PG_GOAL_METRIC_CRR_CLICKS,
  [ROAS_CLICKS]: PG_GOAL_METRIC_ROAS_CLICKS,
};

export class TileData {
  originalValue: number;
  value: { raw: string; pretty: string };
  valueForUserCurrency: { raw: string; pretty: string };
  selectable: boolean;
  selected: boolean;
  key: string;
  growth?: string;
  text: string;
  isPositive?: boolean;
}
export const enum ScenariosType {
  SCENARIOS = 'SCENARIOS',
  NEW_PG = 'NEW-PG',
  UPDATE_PG = 'UPDATE-PG',
}

export interface ViewCharts {
  CLICK: ViewChart;
  CONVERSION?: ViewChart;
  PROFIT?: ViewChart;
  REVENUE?: ViewChart;
  VCPM?: ViewChart;
}

export interface ViewChart {
  chart: {
    menu: {
      defaultOptions: [string, string];
      selected: [string?, string?];
      xAxisMenu: string[];
      yAxisMenu: string[];
    };
  };
  table: {
    menu: string[];
  };
}

export interface ScenariosInfo {
  PGid?: string | number;
  type?: 'CLICK' | 'CONVERSION' | 'REVENUE' | 'PROFIT' | 'VCPM';
  realType?: CampaignOptimizationValue;
  goal?: string;
  goalValue?: number;
  newGoal?: { type: string; value: number };
  goalCurrency?: Currency;
  PGname?: string;
  allGoals?: Goal[];
  currentValues?: GoalMetrics;
}

const marker = ChartMarkers[2];
export const ChartConfig: Highcharts.Options = {
  ...ChartResetDefaults,
  chart: { spacingLeft: 0, spacingRight: 0, height: 320, marginLeft: CHART_SCALE_WIDTH * 1.5, marginRight: 10 },
  exporting: { filename: `${location.pathname}${location.search}`, sourceWidth: window.innerWidth, scale: 1 },
  yAxis: {
    min: 0,
    title: { text: '' },
    ...AxisGridInfo,
    width: CHART_SCALE_WIDTH,
    lineColor: CHART_COLORS.grindLines,
    lineWidth: 1,
  },
  xAxis: {
    ...AxisGridInfo,
    gridLineDashStyle: 'ShortDash',
    lineColor: CHART_COLORS.grindLines,
    lineWidth: 1,
    crosshair: true,
    tickmarkPlacement: 'on',
    title: { text: '' },
  },
  series: [
    <any>{
      data: [],
      type: 'spline',
      lineWidth: 2,
      tooltip: {},
      lineColor: '#7EB6EA',
      color: '#009ee0',
      zIndex: 1,
      pointPlacement: 'on',
      marker: { enabled: true, symbol: marker.symbol, radius: marker.radius, lineWidth: 1 },
    },
    {
      data: [],
      type: 'areasplinerange',
      lineWidth: 0,
      linkedTo: ':previous',
      color: '#7EB6EA',
      fillOpacity: 0.3,
      zIndex: 0,
      states: { hover: { enabled: false } },
      enableMouseTracking: false,
      marker: { enabled: false },
      pointPlacement: 'on',
    },
  ],
};
